import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';
import { OverlayLoaderComponent } from '../../common/loader/overlay-loader/overlay-loader.component';
import { PlanPricingService } from '../../../services/plan-pricing/plan-pricing.service';
import { RazorpayLoaderService } from '../../../services/plan-pricing/razorpay-loader.service';
import { UserAccountService } from '../../../services/user-account/user-account.service';
import { BillingDetailsDialogComponent } from '../billing-details-dialog/billing-details-dialog.component';
import { BillingDetails } from '../../../models/billing-details.model';

declare var Razorpay: any;

@Component({
  selector: 'app-plan-pricing',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    OverlayLoaderComponent,
    SidebarComponent,
    BillingDetailsDialogComponent,
  ],
  templateUrl: './plan-pricing.component.html',
  styleUrls: ['./plan-pricing.component.css'],
})
export class PlanPricingComponent implements OnInit, OnDestroy {
  private static readonly GST_RATE = 0.18;
  isLoading: boolean = false;
  processingPlanId: string | null = null;
  plans: any[] = [];
  razorpayConfig: any = null;
  private razorpayInstance: any = null;

  showPaymentSuccessModal: boolean = false;
  transactionId: string = '';
  hasBillingDetails: boolean = false;
  billingDetails: BillingDetails | null = null;
  showBillingModal: boolean = false;
  pendingInvoiceDownload: boolean = false;
  pendingPlan: any = null;
  billingDialogTitle = 'Add Billing Details';
  billingDialogSubtitle =
    'Add billing details before purchasing a plan. These will appear on your invoice.';
  isInvoiceBusy: boolean = false;

  constructor(
    private planPricingService: PlanPricingService,
    private razorpayLoader: RazorpayLoaderService,
    private userAccountService: UserAccountService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadPlans();
    this.loadRazorpayConfig();
    this.loadBillingDetails();
  }

  ngOnDestroy(): void {
    // Clean up Razorpay instance if modal is still open
    if (this.razorpayInstance) {
      try {
        this.razorpayInstance.close();
      } catch (error) {
        console.error('Error closing Razorpay modal:', error);
      }
      this.razorpayInstance = null;
    }
    this.processingPlanId = null;
  }

  loadRazorpayConfig(): void {
    this.planPricingService.getRazorpayConfig().subscribe({
      next: (response) => {
        this.razorpayConfig = response?.data || response;
      },
      error: (error) => {
        console.error('Error loading Razorpay config:', error);
      },
    });
  }

  isPlanProcessing(planId: string): boolean {
    return this.processingPlanId === planId;
  }

  resetProcessingState(): void {
    this.processingPlanId = null;
    this.razorpayInstance = null;
    this.cdr.detectChanges();
  }

  closePaymentSuccessModal(): void {
    this.showPaymentSuccessModal = false;
    this.transactionId = '';
    this.pendingInvoiceDownload = false;
  }

  copyTransactionId(): void {
    if (this.transactionId) {
      navigator.clipboard.writeText(this.transactionId).then(
        () => {
          // Optionally show a brief success message
        },
        (err) => {
          console.error('Failed to copy transaction ID:', err);
        },
      );
    }
  }

  backToHome(): void {
    this.closePaymentSuccessModal();
    this.router.navigate(['/osint-number-search']);
  }

  loadPlans(): void {
    this.isLoading = true;
    this.planPricingService.getPlans().subscribe({
      next: (response) => {
        const apiPlans = response?.data || [];
        this.plans = apiPlans
          .filter((plan: any) => plan.is_active)
          .map((plan: any) => this.transformPlan(plan))
          .sort((a: any, b: any) => a.displayOrder - b.displayOrder);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading plans:', error);
        this.isLoading = false;
        // Optionally set default plans or show error message
      },
    });
  }

  transformPlan(apiPlan: any): any {
    const features = this.buildFeaturesList(apiPlan);
    const credits = this.buildCreditsDisplay(apiPlan);
    const price = parseFloat(apiPlan.plan_price || '0');
    const gstAmount = this.calculateGstAmount(price);
    const totalPrice = this.roundToTwoDecimals(price + gstAmount);

    return {
      id: apiPlan.plan_id,
      planCode: apiPlan.plan_code,
      title: apiPlan.plan_name,
      description: apiPlan.description,
      price: price,
      gstAmount: gstAmount,
      totalPrice: totalPrice,
      credits: credits,
      features: features,
      // maxSearchesPerDay: apiPlan.max_searches_per_day,
      totalCredits: apiPlan.total_credits,
      validityPeriod: apiPlan.validity_period,
      isPopular: apiPlan.plan_code?.toLowerCase() === 'premium', // Mark Premium plan as popular
      buttonText: this.getButtonText(apiPlan.plan_code),
      buttonClass:
        apiPlan.plan_code?.toLowerCase() === 'premium'
          ? 'btn-premium'
          : 'btn-basic',
      displayOrder: apiPlan.display_order,
      // Keep original API data for reference
      originalData: apiPlan,
    };
  }

  buildFeaturesList(plan: any): string[] {
    const features: string[] = [];

    if (plan.allows_basic_search) {
      features.push('Basic search lookup');
    }

    if (plan.allows_face_search) {
      features.push('Face/Photo-based search');
    }

    if (plan.allows_osint_industries) {
      features.push('Deep OSINT Industries search');
    }

    if (plan.allows_leak_search) {
      features.push('Leak/Breach search (dark web + exposed databases)');
      features.push('Password breach information');
      features.push('Email alerts for matched breach data');
    }

    if (plan.allows_vehicle_search) {
      features.push('Vehicle search');
    }

    if (plan.allows_virtual_numbers) {
      features.push('Virtual number search');
    }

    if (plan.allows_pan_search) {
      features.push('PAN search');
    }

    if (plan.allows_name_search) {
      features.push('Name search');
    }

    // if (plan.max_searches_per_day !== null) {
    //   features.push(`Up to ${plan.max_searches_per_day} searches/day`);
    // } else {
    //   features.push('Unlimited searches/day');
    // }

    if (plan.display_order >= 3) {
      features.push('Priority results');
      features.push('Export full data logs');
    }

    return features;
  }

  buildCreditsDisplay(plan: any): string {
    // Use combined_search_credit_cost if available and > 0, otherwise show individual costs
    if (
      plan.combined_search_credit_cost &&
      plan.combined_search_credit_cost > 0
    ) {
      return `${plan.combined_search_credit_cost} Credit${
        plan.combined_search_credit_cost > 1 ? 's' : ''
      } / search`;
    }

    // Build credit display from individual costs
    const creditCosts: string[] = [];

    if (plan.basic_search_credit_cost > 0) {
      creditCosts.push(`Basic: ${plan.basic_search_credit_cost}`);
    }
    if (plan.osint_search_credit_cost > 0) {
      creditCosts.push(`OSINT: ${plan.osint_search_credit_cost}`);
    }
    if (plan.leak_search_credit_cost > 0) {
      creditCosts.push(`Leak: ${plan.leak_search_credit_cost}`);
    }
    if (plan.vehicle_search_credit_cost > 0) {
      creditCosts.push(`Vehicle: ${plan.vehicle_search_credit_cost}`);
    }

    if (creditCosts.length > 0) {
      return creditCosts.join(', ') + ' Credits';
    }

    return 'Variable credits';
  }

  getButtonText(planCode: string): string {
    const buttonTexts: { [key: string]: string } = {
      starter: 'Start with Starter',
      premium: 'Choose Premium',
      platinum: 'Go Platinum',
      basic: 'Start with Basic',
      medium: 'Upgrade to Pro',
      standard: 'Get Advanced',
      elite: 'Get Elite',
    };
    return buttonTexts[planCode.toLowerCase()] || 'Get Started';
  }

  selectPlan(plan: any): void {
    if (this.processingPlanId) {
      return;
    }

    if (!this.razorpayConfig || !this.razorpayConfig.key_id) {
      this.toastr.error(
        'Payment gateway not configured. Please try again later.',
      );
      return;
    }

    if (!plan.totalPrice || plan.totalPrice <= 0) {
      this.toastr.error('Invalid plan price.');
      return;
    }

    if (!this.hasBillingDetails) {
      this.userAccountService.getBillingDetails().subscribe({
        next: (response) => {
          this.hasBillingDetails = Boolean(response?.data?.exists);
          this.billingDetails = response?.data?.billing_details || null;
          if (this.hasBillingDetails) {
            this.startPayment(plan);
            return;
          }
          this.openBillingForPurchase(plan);
        },
        error: () => this.openBillingForPurchase(plan),
      });
      return;
    }

    this.startPayment(plan);
  }

  private openBillingForPurchase(plan: any): void {
    this.pendingPlan = plan;
    this.pendingInvoiceDownload = false;
    this.billingDialogTitle = 'Add Billing Details';
    this.billingDialogSubtitle =
      'Add billing details before purchasing a plan. These will appear on your invoice.';
    this.showBillingModal = true;
  }

  private startPayment(plan: any): void {
    const amountInRupees = this.roundToTwoDecimals(Number(plan.totalPrice));

    this.processingPlanId = plan.id;
    this.planPricingService
      .createOrder(amountInRupees, plan.id, 'credit_topup')
      .subscribe({
        next: (response) => {
          const orderData = response?.data;
          if (!orderData || !orderData.order_id) {
            this.toastr.error('Invalid order data received.');
            this.resetProcessingState();
            return;
          }
          orderData.amount = Number(orderData.amount);
          this.initiateRazorpayPayment(orderData, plan);
        },
        error: (error) => {
          console.error('Error creating order:', error);
          this.resetProcessingState();
          if (
            error?.status === 409 ||
            error?.error?.data?.needs_billing_details
          ) {
            this.hasBillingDetails = false;
            this.openBillingForPurchase(plan);
            return;
          }
          this.toastr.error(
            error.error?.message_key ||
              'Failed to create payment order. Please try again.',
          );
        },
      });
  }

  initiateRazorpayPayment(orderData: any, plan: any): void {
    if (!orderData.order_id || !orderData.amount) {
      this.toastr.error('Invalid order data received.');
      this.resetProcessingState();
      return;
    }

    // Use the amount from API response (already in paisa)
    // The API returns amount in smallest currency unit (paisa for INR)
    // Ensure amount is a number
    const amountInPaisa = Number(orderData.amount);

    const options = {
      key: this.razorpayConfig.key_id,
      amount: amountInPaisa, // Amount in paisa
      currency: this.razorpayConfig.currency || 'INR',
      name: 'Ryno Subscription',
      description: `Purchase ${plan.title} Plan`,
      order_id: orderData.order_id,
      handler: (response: any) => {
        this.handlePaymentSuccess(response, plan);
      },
      prefill: {
        // You can prefill user details if available
        name: '',
        email: '',
        contact: '',
      },
      theme: {
        color: '#3399cc',
      },
      modal: {
        ondismiss: () => {
          console.log('Payment modal closed');
          this.resetProcessingState();
        },
      },
    };

    this.razorpayLoader.load().then(
      () => {
        try {
          const razorpay = new Razorpay(options);
          this.razorpayInstance = razorpay;

          razorpay.on('payment.failed', (response: any) => {
            console.error('Payment failed:', response);
            this.resetProcessingState();
            this.toastr.error(
              'Payment failed. Please try again.',
              'Payment Failed',
            );
          });

          razorpay.open();
        } catch (error) {
          console.error('Error initializing Razorpay:', error);
          this.toastr.error('Failed to initialize payment gateway.');
          this.resetProcessingState();
        }
      },
      (error) => {
        console.error('Error loading Razorpay:', error);
        this.toastr.error('Failed to load payment gateway.');
        this.resetProcessingState();
      },
    );
  }

  handlePaymentSuccess(response: any, plan: any): void {
    if (
      !response.razorpay_payment_id ||
      !response.razorpay_order_id ||
      !response.razorpay_signature
    ) {
      this.toastr.error('Invalid payment response.');
      this.resetProcessingState();
      return;
    }

    this.planPricingService
      .verifyPayment(
        response.razorpay_order_id,
        response.razorpay_payment_id,
        response.razorpay_signature,
      )
      .subscribe({
        next: (verifyResponse) => {
          this.resetProcessingState();

          // Extract transaction ID from response
          this.transactionId =
            verifyResponse?.data?.transaction_id ||
            verifyResponse?.data?.order_id ||
            response.razorpay_order_id ||
            '';

          // Show payment success modal
          this.showPaymentSuccessModal = true;

          console.log(verifyResponse);
        },
        error: (error) => {
          console.error('Error verifying payment:', error);
          this.toastr.error(
            error.error?.message_key ||
              'Payment verification failed. Please contact support.',
            'Verification Failed',
          );
          this.resetProcessingState();
        },
      });
  }

  private calculateGstAmount(price: number): number {
    return this.roundToTwoDecimals(price * PlanPricingComponent.GST_RATE);
  }

  private roundToTwoDecimals(amount: number): number {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
  }

  loadBillingDetails(): void {
    this.userAccountService.getBillingDetails().subscribe({
      next: (response) => {
        this.hasBillingDetails = Boolean(response?.data?.exists);
        this.billingDetails = response?.data?.billing_details || null;
      },
      error: () => {
        this.hasBillingDetails = false;
      },
    });
  }

  onViewInvoice(): void {
    this.requestInvoice(false);
  }

  onDownloadInvoice(): void {
    this.requestInvoice(true);
  }

  closeBillingModal(): void {
    this.showBillingModal = false;
    this.pendingInvoiceDownload = false;
    this.pendingPlan = null;
  }

  onBillingDetailsSaved(details: BillingDetails): void {
    this.hasBillingDetails = true;
    this.billingDetails = details;
    this.showBillingModal = false;

    const planToPurchase = this.pendingPlan;
    this.pendingPlan = null;
    if (planToPurchase) {
      this.startPayment(planToPurchase);
      return;
    }

    if (!this.transactionId) {
      this.pendingInvoiceDownload = false;
      return;
    }

    const shouldDownload = this.pendingInvoiceDownload;
    this.pendingInvoiceDownload = false;
    this.fetchInvoice(shouldDownload);
  }

  private requestInvoice(download: boolean): void {
    if (!this.transactionId) {
      this.toastr.error('Transaction ID is missing.');
      return;
    }
    if (!this.hasBillingDetails) {
      this.pendingInvoiceDownload = download;
      this.billingDialogTitle = 'Add Billing Details';
      this.billingDialogSubtitle =
        'Add billing details to generate your invoice. You can update these later from My Subscription.';
      this.showBillingModal = true;
      return;
    }
    this.fetchInvoice(download);
  }

  private fetchInvoice(download: boolean): void {
    this.isInvoiceBusy = true;
    this.userAccountService.getPaymentInvoice(this.transactionId, download).subscribe({
      next: (response) => {
        const blob = response.body;
        if (!blob) {
          this.toastr.error('Invoice could not be generated.');
          this.isInvoiceBusy = false;
          return;
        }
        const filename = this.userAccountService.getFilenameFromResponse(
          response,
          'invoice.pdf',
        );
        if (download) {
          this.userAccountService.saveBlobFile(blob, filename);
        } else {
          this.userAccountService.openBlobInNewTab(blob);
        }
        this.isInvoiceBusy = false;
      },
      error: async (error) => {
        this.isInvoiceBusy = false;
        if (error?.status === 409) {
          this.pendingInvoiceDownload = download;
          this.billingDialogTitle = 'Add Billing Details';
          this.billingDialogSubtitle =
            'Add billing details to generate your invoice. You can update these later from My Subscription.';
          this.showBillingModal = true;
          return;
        }
        this.toastr.error(
          await this.userAccountService.parseErrorMessage(
            error,
            'Unable to load invoice. Please try again.',
          ),
        );
      },
    });
  }
}
